import { faMeta } from '@fortawesome/free-brands-svg-icons';
import { faRectangleAd } from '@fortawesome/pro-regular-svg-icons';
import { Stack } from '@mantine/core';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { RowRenderProps } from 'react-table';

import type {
  AdTagCreative,
  Campaigns as CampaignsCollection,
  Creative,
  DisplayCreative,
} from '@feathr/blackbox';
import { isAdTagCreative } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { Button, CreativeListItem, Icon, TableColumnHeader, Time } from '@feathr/components';
import { CampaignTypeChip } from '@feathr/extender/components';
import {
  getCreativeAspectRatio,
  getCreativeDimensions,
  getCreativesStepHash,
  getCreativeThumbnailUrl,
  hasStoredThumbnail,
  isMetaCreative,
  isVideoCreative,
} from '@feathr/extender/components/Creatives/Creatives.utils';
import { TimeFormat } from '@feathr/hooks';

import ProjectCreativesOptions from '../ProjectCreativesOptions';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from '../ProjectCreativesPage.css';

import noImg from '@feathr/extender/images/no-img.png';

interface IRow extends RowRenderProps {
  original: Creative;
}

interface IProjectCreativesColumnsProps {
  Campaigns: CampaignsCollection;
  localUrl: (url: string) => string;
  onEditCreative: (creative: Creative) => void;
}

function getCreativeTypeIcon(creative: Creative): JSX.Element {
  return <Icon icon={isMetaCreative(creative) ? faMeta : faRectangleAd} size={4} theme={'sky'} />;
}

function ProjectCreativesColumns({
  Campaigns,
  localUrl,
  onEditCreative,
}: IProjectCreativesColumnsProps): Array<IColumn<Creative>> {
  return [
    {
      id: 'name',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: 'Name',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      Cell({ original }: IRow): JSX.Element {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              const displayCreative = original as DisplayCreative;
              const isAdTag = isAdTagCreative(displayCreative);
              const thumbnailUrl = getCreativeThumbnailUrl(displayCreative) || noImg;

              return (
                <CreativeListItem
                  adTagHeight={isAdTag ? original.get('height') : undefined}
                  adTagHtml={isAdTag ? (original as AdTagCreative).get('adtag') : undefined}
                  adTagWidth={isAdTag ? original.get('width') : undefined}
                  aspectRatio={displayCreative.aspectRatio ?? getCreativeAspectRatio(original)}
                  className={styles.creativeListItemCompact}
                  dimensions={displayCreative.dimensions ?? getCreativeDimensions(original)}
                  hasThumbnail={hasStoredThumbnail(displayCreative)}
                  isAdTag={isAdTag}
                  isVideo={isVideoCreative(original)}
                  prefix={getCreativeTypeIcon(original)}
                  specName={original.getTypeLabel()}
                  thumbnailUrl={thumbnailUrl}
                  title={
                    <Button
                      className={styles.creativeTitleButton}
                      name={'edit-creative'}
                      onClick={(): void => onEditCreative(original)}
                      type={'link'}
                    >
                      {original.name}
                    </Button>
                  }
                />
              );
            }}
          </Observer>
        );
      },
    },
    {
      id: 'campaign',
      Header: TableColumnHeader({
        title: 'Campaign',
      }),
      className: tableStyles.cell,
      sortable: false,
      Cell({ original }: IRow): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element | null => {
              const campaignId = original.get('parent');

              if (!campaignId) {
                return null;
              }

              const campaign = Campaigns.get(campaignId);

              if (campaign.isErrored) {
                return null;
              }

              const creativesStepUrl = `${campaign.getItemUrl('edit')}${getCreativesStepHash(campaign)}`;

              return (
                <Stack align={'flex-start'} gap={8}>
                  <Link to={localUrl(creativesStepUrl)}>{campaign.name}</Link>
                  <CampaignTypeChip
                    cls={campaign.get('_cls')}
                    isLoading={campaign.isPending}
                    isMonetization={campaign.isMonetization}
                  />
                </Stack>
              );
            }}
          </Observer>
        );
      },
    },
    {
      id: 'date_last_modified',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: 'Last updated',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      width: 150,
      Cell({ original }: IRow): JSX.Element {
        return (
          <Observer>
            {(): JSX.Element => (
              <Time format={TimeFormat.longDate} timestamp={original.get('date_last_modified')} />
            )}
          </Observer>
        );
      },
    },
    {
      id: 'options',
      Header: TableColumnHeader({
        title: 'Options',
      }),
      sortable: false,
      className: tableStyles.cellCenter,
      width: 80,
      Cell({ original }: IRow): JSX.Element {
        return <ProjectCreativesOptions creative={original} />;
      },
    },
  ];
}

export default ProjectCreativesColumns;
